import { completeOnboarding } from '@services/activity-tracker/activityTrackerService';
import * as api from '@company/services/job/api';
import { ActivityTrackerView } from '@th-types/jobs.type';
import QueryKeys from '@constants/queryKeys';
import { groupBy, sortBy } from 'lodash';
import { AxiosError } from 'axios';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  QueryObserverResult,
  RefetchOptions,
  useQuery,
} from '@tanstack/react-query';
import {
  ActivityDetail,
  ActivityDetailList,
  ActivityStatus,
} from '@th-types/daily-activity.type';
import { JobAssignment } from '@th-types/job.detail.type';
import { useJobContext } from './jobContext';

export interface GroupByActivityType {
  groupedBy: string;
  groupedByActivities: ActivityDetail[];
}

interface IActivityContext {
  upcoming: boolean;
  isChatOpen: boolean;
  isLoadingAction: boolean;
  activitiesFilter: string[];
  viewType: ActivityTrackerView;
  activities?: ActivityDetail[];
  showReviewShiftModal: boolean;
  activitiesQueryKey: string[];
  selectedActivity: ActivityDetail | undefined;
  groupedByDateActivities: GroupByActivityType[];
  groupedByWorkerActivities: GroupByActivityType[];
  setUpcoming: (state: boolean) => void;
  setIsChatOpen: (isOpen: boolean) => void;
  setIsLoadingAction: (state: boolean) => void;
  setActivitiesFilter: (filters: string[]) => void;
  setShowReviewShiftModal: (show: boolean) => void;
  setViewType: (type: ActivityTrackerView) => void;
  getActivityStatus: (item: ActivityDetail) => string;
  isShiftUnPayable: (activity: ActivityDetail) => boolean;
  setActivities: (activities: ActivityDetail[]) => void;
  setSelectedActivity: (activity: ActivityDetail | undefined) => void;
  completeOnboardingAction: (
    jobAssignments: JobAssignment[],
    completed?: boolean
  ) => void;
  error?: AxiosError<unknown, unknown> | null;
  isLoading?: boolean;
  refetch: (
    options?: RefetchOptions | undefined
  ) => Promise<
    QueryObserverResult<ActivityDetailList, AxiosError<unknown, unknown>>
  >;
  isRefetching: boolean;
  enableDataFetching: () => void;
}

const ActivityContext: IActivityContext | any = createContext({});
interface ProviderProps {
  children: React.ReactNode;
}
export function ActivityProvider({ children }: ProviderProps) {
  const { activeJobId, job } = useJobContext();

  const [viewType, setViewType] = useState<ActivityTrackerView>(
    ActivityTrackerView.DATE
  );
  const [upcoming, setUpcoming] = useState(false);
  const [activities, setActivities] = useState<ActivityDetail[]>([]);
  const [selectedActivity, setSelectedActivity] = useState<
    ActivityDetail | undefined
  >();
  const [groupedByDateActivities, setGroupedByDateActivities] = useState<
    GroupByActivityType[]
  >([]);
  const [groupedByWorkerActivities, setGroupedByWorkerActivities] = useState<
    GroupByActivityType[]
  >([]);
  const [activitiesFilter, setActivitiesFilter] = useState<string[]>([]);
  const [isLoadingAction, setIsLoadingAction] = useState(false);
  const [showReviewShiftModal, setShowReviewShiftModal] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [shouldFetchData, setShouldFetchData] = useState(false);

  const activitiesQueryKey = useMemo(() => {
    return [`${QueryKeys.COMPANY_JOB_ACTIVITY_TRACKER}_${activeJobId}`];
  }, [activeJobId]);

  const { isLoading, error, data, refetch, isRefetching } = useQuery<
    ActivityDetailList,
    AxiosError
  >({
    queryKey: activitiesQueryKey,
    queryFn: async () => await api.fetchActivityTrackerList(activeJobId),
    enabled: shouldFetchData && job !== null && activeJobId > 0,
  });

  const enableDataFetching = useCallback(() => {
    setShouldFetchData(true);
  }, []);

  useEffect(() => {
    if (job?.id && shouldFetchData) {
      refetch();
    }
  }, [job?.id, refetch, shouldFetchData]);

  useEffect(() => {
    const getGroupByDateData = () => {
      const groupByDateData: GroupByActivityType[] = [];
      if (data) {
        const groupByDate = groupBy(
          sortBy(data.activities, (d) => d.shiftPosition.date),
          (item) => item.shiftPosition.date
        );
        Object.entries(groupByDate).forEach(([key, value]) => {
          groupByDateData.push({ groupedBy: key, groupedByActivities: value });
        });
      }
      return groupByDateData;
    };

    const getGroupByWorkerData = () => {
      const groupByWorkerData: GroupByActivityType[] = [];
      if (data) {
        const groupByWorker = groupBy(
          sortBy(
            data.activities,
            (item) => item.shiftPosition.jobAssignment?.worker.fullName
          ),
          (item) => item.shiftPosition.jobAssignment?.worker.fullName
        );
        Object.entries(groupByWorker).forEach(([key, value]) => {
          groupByWorkerData.push({
            groupedBy: key,
            groupedByActivities: value,
          });
        });
      }
      return groupByWorkerData;
    };
    if (data?.activities) {
      setActivities(data.activities);
      setGroupedByDateActivities(getGroupByDateData());
      setGroupedByWorkerActivities(getGroupByWorkerData());
    } else if (activities) {
      setGroupedByDateActivities(getGroupByDateData());
      setGroupedByWorkerActivities(getGroupByWorkerData());
    }
  }, [data, viewType, activities]);

  const getActivityStatus = useCallback((item: ActivityDetail) => {
    if (item.timeSheet && item.dailyActivity && !item.shiftPosition.isPaid) {
      return ActivityStatus.TO_APPROVE.toString();
    }
    if (!item.dailyActivity || !item.timeSheet) {
      return ActivityStatus.IN_PROGRESS.toString();
    }
    return ActivityStatus.CANCELLED.toString();
  }, []);

  const isShiftUnPayable = useCallback((activity: ActivityDetail) => {
    return activity.shiftPosition.isPaid;
  }, []);

  const completeOnboardingAction = useCallback(
    async (jobAssignments: JobAssignment[], completed: boolean = true) => {
      try {
        if (activities) {
          setIsLoadingAction(true);
          setActivities(
            await completeOnboarding(jobAssignments, activities, completed)
          );
        }
      } catch (err) {
        console.log(
          `Occurred an error trying to mark onboardings as completed`,
          err
        );
      } finally {
        setIsLoadingAction(false);
      }
    },
    [activities]
  );

  const contextValue: IActivityContext = useMemo(() => {
    return {
      error,
      viewType,
      upcoming,
      isLoading,
      activities,
      isChatOpen,
      isRefetching,
      isLoadingAction,
      activitiesFilter,
      selectedActivity,
      activitiesQueryKey,
      showReviewShiftModal,
      groupedByDateActivities,
      groupedByWorkerActivities,
      refetch,
      setUpcoming,
      setViewType,
      setIsChatOpen,
      setActivities,
      isShiftUnPayable,
      getActivityStatus,
      setIsLoadingAction,
      setActivitiesFilter,
      setSelectedActivity,
      setShowReviewShiftModal,
      completeOnboardingAction,
      enableDataFetching,
    };
  }, [
    error,
    viewType,
    upcoming,
    isLoading,
    activities,
    isChatOpen,
    isRefetching,
    isLoadingAction,
    activitiesFilter,
    selectedActivity,
    activitiesQueryKey,
    showReviewShiftModal,
    groupedByDateActivities,
    groupedByWorkerActivities,
    refetch,
    setUpcoming,
    setViewType,
    setIsChatOpen,
    setActivities,
    isShiftUnPayable,
    getActivityStatus,
    setIsLoadingAction,
    setActivitiesFilter,
    setSelectedActivity,
    setShowReviewShiftModal,
    completeOnboardingAction,
    enableDataFetching,
  ]);
  return (
    <ActivityContext.Provider value={contextValue}>
      {children}
    </ActivityContext.Provider>
  );
}

export function useActivityContext() {
  const context: IActivityContext = useContext(ActivityContext);
  if (context === undefined) {
    throw new Error(
      'useActivityContext must be used within a ActivityProvider. Wrap a parent component in <ActivityProvider> to fix this error.'
    );
  }
  return context;
}
